import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box, Typography, Paper, Divider, Skeleton, Card,
  List, ListItem, ListItemText, IconButton
} from '@mui/material'
import { FamilyRestroom, ArrowBack, Person } from '@mui/icons-material'
import { useAuth } from '@/contexts/AuthContext'
import { useSnackbar } from 'notistack'
import { apiClient } from '@/services/apiClient'
import { PublicProfile } from '@/types'

export default function UserProfile() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [profile, setProfile] = useState<PublicProfile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) return

    // Redirect to own profile page if viewing self
    if (user?.userId === userId) {
      navigate('/profile', { replace: true })
      return
    }

    const loadProfile = async () => {
      try {
        const data = await apiClient.get<PublicProfile>(`/api/account/${userId}/public-profile`)
        setProfile(data)
      } catch {
        enqueueSnackbar('Failed to load profile', { variant: 'error' })
        navigate(-1)
      } finally {
        setLoading(false)
      }
    }
    loadProfile()
  }, [userId, user, enqueueSnackbar, navigate])

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width="50%" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={80} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={80} />
      </Box>
    )
  }

  if (!profile) return null

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', pb: 10 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {profile.displayName}
        </Typography>
      </Box>

      {/* About Me */}
      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Person fontSize="small" color="action" />
          <Typography variant="subtitle2" color="text.secondary">About Me</Typography>
        </Box>
        {profile.aboutMe ? (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{profile.aboutMe}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            This user hasn't added anything about themselves yet.
          </Typography>
        )}
      </Paper>

      {/* Family */}
      {profile.family && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <FamilyRestroom color="action" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{profile.family.name}</Typography>
            </Box>

            {profile.family.memberDisplayNames.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Family Members
                </Typography>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <List disablePadding>
                    {profile.family.memberDisplayNames.map((name, index) => (
                      <ListItem key={index} divider={index < profile.family!.memberDisplayNames.length - 1}>
                        <ListItemText primary={name} />
                      </ListItem>
                    ))}
                  </List>
                </Card>
              </>
            )}

            {profile.family.additionalMemberNames.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Additional Members
                </Typography>
                <Card variant="outlined">
                  <List disablePadding>
                    {profile.family.additionalMemberNames.map((name, index) => (
                      <ListItem key={index} divider={index < profile.family!.additionalMemberNames.length - 1}>
                        <ListItemText primary={name} />
                      </ListItem>
                    ))}
                  </List>
                </Card>
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}
